import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  CheckCircle, XCircle, ArrowRight, Zap, Shield, Clock, 
  FileText, Bell, BarChart3, Calculator, CreditCard, Sparkles, Users
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SmartFactureVsFreebe = () => {
  const comparisons = [
    { feature: "Format Factur-X conforme 2026", smartfacture: true, competitor: false },
    { feature: "Relances automatiques impayés", smartfacture: true, competitor: true },
    { feature: "Scanner IA de factures", smartfacture: true, competitor: false },
    { feature: "Module URSSAF intégré", smartfacture: true, competitor: true },
    { feature: "Rapprochement bancaire", smartfacture: true, competitor: true },
    { feature: "Export comptable CSV", smartfacture: true, competitor: true },
    { feature: "Dashboard temps réel", smartfacture: true, competitor: true },
    { feature: "Support prioritaire", smartfacture: true, competitor: true },
    { feature: "Essai gratuit 7 jours", smartfacture: true, competitor: true },
    { feature: "À partir de 14€/mois", smartfacture: true, competitor: false },
  ];

  const smartfactureAdvantages = [
    {
      icon: FileText,
      title: "Factur-X obligatoire 2026",
      description: "SmartFacture génère des factures au format Factur-X dès aujourd'hui. Freebe n'offre que le format PDF classique, non conforme à la réforme."
    },
    {
      icon: Sparkles,
      title: "Intelligence artificielle",
      description: "Notre scanner IA extrait automatiquement les données de vos factures fournisseurs. Une fonctionnalité unique absente chez Freebe."
    },
    {
      icon: Users,
      title: "Collaboration multi-utilisateurs",
      description: "Invitez votre comptable ou associés avec des droits personnalisés. Freebe reste limité à un seul utilisateur."
    },
    {
      icon: CreditCard,
      title: "Tarif compétitif",
      description: "SmartFacture démarre à 14€/mois avec plus de fonctionnalités. Freebe est souvent plus cher pour moins de features."
    }
  ];

  const faqs = [
    {
      q: "Freebe ou SmartFacture AI pour les freelances ?",
      a: "SmartFacture AI est plus adapté aux freelances qui veulent anticiper la réforme 2026 avec le format Factur-X natif. Freebe reste sur un format PDF classique."
    },
    {
      q: "SmartFacture AI remplace-t-il Freebe ?",
      a: "Oui, SmartFacture AI couvre toutes les fonctionnalités de Freebe et ajoute le Factur-X, le scanner IA et le multi-utilisateurs."
    },
    {
      q: "Comment migrer de Freebe à SmartFacture ?",
      a: "Exportez vos données depuis Freebe et importez-les dans SmartFacture. Notre support vous accompagne gratuitement."
    },
    {
      q: "SmartFacture est-il plus cher que Freebe ?",
      a: "Non, SmartFacture démarre à 14€/mois avec le plan Starter, souvent moins cher que Freebe pour plus de fonctionnalités."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>SmartFacture AI vs Freebe : Comparatif 2026 | Le meilleur choix freelance</title>
        <meta name="description" content="Comparatif SmartFacture AI vs Freebe pour freelances et auto-entrepreneurs. Fonctionnalités, prix, conformité 2026. Découvrez quelle solution choisir." />
        <link rel="canonical" href="https://smartfacture-ai.com/comparaison/smartfacture-vs-freebe" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <Link to="/register">
              <Button className="bg-blue-600 hover:bg-blue-700">
                Essai gratuit
              </Button>
            </Link>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-violet-600 to-purple-600 text-white py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              Comparatif Freelance
            </Badge>
            <h1 className="text-3xl lg:text-5xl font-bold mb-6">
              SmartFacture AI vs Freebe
            </h1>
            <p className="text-xl text-violet-100 max-w-2xl mx-auto">
              Vous êtes freelance et hésitez entre Freebe et SmartFacture AI ? Découvrez notre comparatif complet pour faire le bon choix.
            </p>
          </div>
        </section>

        <div className="max-w-6xl mx-auto px-4 py-12">
          {/* Comparison Table */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Comparatif des fonctionnalités
            </h2>
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="grid grid-cols-3 bg-slate-100 p-4 font-semibold text-slate-700">
                <div>Fonctionnalité</div>
                <div className="text-center text-blue-600">SmartFacture AI</div>
                <div className="text-center">Freebe</div>
              </div>
              {comparisons.map((item, idx) => (
                <div key={idx} className={`grid grid-cols-3 p-4 items-center ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                  <div className="text-slate-700">{item.feature}</div>
                  <div className="text-center">
                    {item.smartfacture ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                    )}
                  </div>
                  <div className="text-center">
                    {item.competitor ? (
                      <CheckCircle className="h-6 w-6 text-emerald-500 mx-auto" />
                    ) : (
                      <XCircle className="h-6 w-6 text-red-400 mx-auto" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Advantages */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Les avantages de SmartFacture AI sur Freebe
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {smartfactureAdvantages.map((adv, idx) => {
                const Icon = adv.icon;
                return (
                  <div key={idx} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200">
                    <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-violet-600" />
                    </div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{adv.title}</h3>
                    <p className="text-slate-600">{adv.description}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA */}
          <section className="bg-gradient-to-r from-violet-600 to-purple-500 rounded-2xl p-8 lg:p-12 text-white text-center mb-16">
            <h2 className="text-2xl lg:text-3xl font-bold mb-4">
              Passez à SmartFacture AI
            </h2>
            <p className="text-violet-100 mb-8 max-w-2xl mx-auto">
              Faites comme des milliers de freelances : choisissez la solution conforme 2026 avec l'IA intégrée.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/register"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-violet-600 hover:bg-violet-50 font-semibold rounded-xl transition-colors"
              >
                Essai gratuit 7 jours
                <ArrowRight className="h-5 w-5" />
              </Link>
              <Link 
                to="/demo"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-white text-white hover:bg-white/10 font-semibold rounded-xl transition-colors"
              >
                Voir la démo
              </Link>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl font-bold text-slate-900 text-center mb-8">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Other Comparisons */}
          <section>
            <h2 className="text-xl font-bold text-slate-900 text-center mb-6">
              Autres comparatifs
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/comparaison/smartfacture-vs-indy" className="text-blue-600 hover:underline">
                SmartFacture vs Indy
              </Link>
              <Link to="/comparaison/smartfacture-vs-tiime" className="text-blue-600 hover:underline">
                SmartFacture vs Tiime
              </Link>
            </div>
          </section>
        </div>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SmartFactureVsFreebe;
